// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import { useBlogSimilarArticlesDataView } from "@store/Framework/Store/Data/BlogArticle/BlogArticleHooks";
import loadBlogSimilarArticles from "@store/Framework/Store/Starter/Data/Blog/Handlers/LoadBlogSimilarArticles";
import BlogTileList, { BlogTileListPresentationProps } from "@store/Library/Components/Toolkit/Blog/BlogTileList";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { blogProps } from "@store/Themes/common/props/blog";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import uniqueId from "lodash/uniqueId";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    quantity = "quantity",
    subheading = "subheading",
    title = "title",
    titleAlignment = "titleAlignment",
}

const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);

interface BlogArticleSimilarArticlesProps extends WidgetProps {
    fields: {
        [fields.quantity]: number;
        [fields.subheading]: string;
        [fields.title]: string;
        [fields.titleAlignment]: string;
    };
}

export interface BlogArticleSimilarArticlesStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    subheading?: TypographyPresentationProps;
    blogTileList?: BlogTileListPresentationProps;
}

export const blogArticleSimilarArticlesStyles: BlogArticleSimilarArticlesStyles = {
    innerWrapper: {
        css: css`
            ${siteWidthStyles()}
            ${x => breakpointValues(x.theme.design.layout.spacing.content, ["padding-top", "padding-bottom"])}
        `,
    },
    title: { variant: "h2" },
    subheading: blogProps.blockSubheading,
};

const styles = blogArticleSimilarArticlesStyles;

const BlogArticleSimilarArticles: FC<BlogArticleSimilarArticlesProps> = ({ fields }) => {
    const { quantity, subheading, title, titleAlignment: baseTitleAlignment } = fields;
    const titleAlignment = titleAlignmentField.parse(baseTitleAlignment);
    const [cacheInvalidationId] = useState(`cacheId-${uniqueId()}`);
    const { isInShell } = useShellContext();
    const dispatch = useDispatch();
    const currentPage = useCurrentPage();
    const baseBlogSimilarArticlesDataView = useBlogSimilarArticlesDataView(
        quantity,
        isInShell ? cacheInvalidationId : undefined,
    );
    const blogSimilarArticlesDataView = usePersistentDataView(baseBlogSimilarArticlesDataView);
    const { isLoading, value: articles } = blogSimilarArticlesDataView;
    const shouldLoadArticles =
        (!isLoading && !articles) || (typeof articles !== "undefined" && quantity <= articles.length && !isLoading);

    useEffect(() => {
        if (shouldLoadArticles) {
            dispatch(
                loadBlogSimilarArticles({
                    nodeId: currentPage.nodeId,
                    quantity,
                    cacheInvalidationId: isInShell ? cacheInvalidationId : undefined,
                }),
            );
        }
    }, [quantity]);

    const links: SafeDictionary<PageLinkModel> =
        useAppSelector(state =>
            articles?.reduce(
                (links, page) => ({
                    ...links,
                    [page.nodeId]: getPageLinkByNodeId(state, page.nodeId),
                    [page.parentId]: getPageLinkByNodeId(state, page.parentId),
                }),
                {},
            ),
        ) || {};

    const titleStyles = useMemo(() => {
        return mergeProps<BlogArticleSimilarArticlesStyles["title"]>(styles.title, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.title, titleAlignment]);

    const subheadingStyles = useMemo(() => {
        return mergeProps<BlogArticleSimilarArticlesStyles["subheading"]>(styles.subheading, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.subheading, titleAlignment]);

    if (!articles?.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.innerWrapper}>
                <Typography {...titleStyles}>{title}</Typography>
                <DataViewLoadingOverlay {...styles.loadingOverlay} dataView={blogSimilarArticlesDataView}>
                    {subheading ? <Typography {...subheadingStyles}>{subheading}</Typography> : null}
                    <BlogTileList {...styles.blogTileList} articles={articles} articlesLinks={links} />
                </DataViewLoadingOverlay>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BlogArticleSimilarArticles,
    definition: {
        displayName: "Similar Articles",
        group: constants.widgetGroup.blog,
        allowedContexts: [constants.pageType.blogArticlePage],
        fieldDefinitions: [
            {
                name: fields.quantity,
                displayName: "Number of articles to show",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: 3,
                min: 1,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Similar Articles",
                isRequired: true,
            },
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
            },
            {
                name: fields.subheading,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
